import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import { getConfigUseCases } from '@onboarding/composition';
import type {
  TableServiceDto,
  CashRegisterDto,
  TipDto,
  TaxDto,
  ReceiptDto,
  HubspotPropertiesDto,
} from '@onboarding/application/dtos';
import { useOnboardingWizardContext, WizardActions } from '@onboarding/presentation/context';
import { WIZARD_STEPS, WIZARD_STEP_ORDER } from '@onboarding/domain/value-objects';

const onbLog = logger.forDomain('ONB');
const tracer = trace.getTracer('@parrot-software/observability');

const STEP_ORDER = WIZARD_STEP_ORDER;

const TOTAL_STEPS = STEP_ORDER.length;

/**
 * Main hook for the onboarding wizard.
 * Orchestrates wizard navigation, step saving, and state management.
 *
 * @returns Wizard state, navigation handlers, and saving state
 */
export const useOnboardingWizard = () => {
  const { t } = useTranslation('onboarding');
  const { state, dispatch } = useOnboardingWizardContext();
  const {
    applyTableService,
    applyCashRegister,
    applyTip,
    applyTax,
    saveReceiptHeader,
    updateHubspotProperties,
  } = getConfigUseCases();

  const hubspotMutation = useMutation({
    mutationFn: (dto: HubspotPropertiesDto) => updateHubspotProperties.execute(dto),
  });

  const tableServiceMutation = useMutation({
    mutationFn: (dto: TableServiceDto) => applyTableService.execute(dto),
  });

  const cashRegisterMutation = useMutation({
    mutationFn: (dto: CashRegisterDto) => applyCashRegister.execute(dto),
  });

  const tipMutation = useMutation({
    mutationFn: (dto: TipDto) => applyTip.execute(dto),
  });

  const taxMutation = useMutation({
    mutationFn: (dto: TaxDto) => applyTax.execute(dto),
  });

  const receiptMutation = useMutation({
    mutationFn: (dto: ReceiptDto) => saveReceiptHeader.execute(dto),
  });

  const isCompletionStep = state.currentStep >= TOTAL_STEPS;
  const currentStepId = STEP_ORDER[state.currentStep] ?? null;

  const stepEnteredAtRef = useRef<number>(Date.now());
  const prevStepRef = useRef<number>(state.currentStep);

  useEffect(() => {
    if (prevStepRef.current === state.currentStep) return;

    const fromStep = STEP_ORDER[prevStepRef.current] ?? 'COMPLETION';
    const toStep = STEP_ORDER[state.currentStep] ?? 'COMPLETION';
    const durationMs = Date.now() - stepEnteredAtRef.current;

    onbLog.info('wizard_step_changed', {
      'onb.from_step': fromStep,
      'onb.to_step': toStep,
      'onb.step_duration_ms': durationMs,
    });

    prevStepRef.current = state.currentStep;
    stepEnteredAtRef.current = Date.now();
  }, [state.currentStep]);

  const handleNext = useCallback(
    async (stepData?: Record<string, unknown>) => {
      if (isCompletionStep) return;

      dispatch({ type: WizardActions.NEXT_STEP });

      const span = tracer.startSpan('ONB.wizard_save_step');
      span.setAttribute('onb.step', currentStepId ?? 'unknown');
      span.setAttribute('onb.step_index', state.currentStep);
      const ctx = trace.setSpan(context.active(), span);

      await context.with(ctx, async () => {
        try {
          if (currentStepId === WIZARD_STEPS.RESTAURANT_TYPE && stepData) {
            await hubspotMutation.mutateAsync(stepData as HubspotPropertiesDto);
          }

          // TABLE_SERVICE config is deferred and submitted at wizard completion alongside
          // wizardCompleted. We don't persist the form locally, so sending it on each step
          // navigation can produce stale data if the user revisits this step.

          if (currentStepId === WIZARD_STEPS.CASH_REGISTER && stepData) {
            await cashRegisterMutation.mutateAsync(stepData as CashRegisterDto);
          }

          if (currentStepId === WIZARD_STEPS.TIP && stepData) {
            await tipMutation.mutateAsync(stepData as TipDto);
          }

          if (currentStepId === WIZARD_STEPS.TAX && stepData) {
            await taxMutation.mutateAsync(stepData as TaxDto);
          }

          if (currentStepId === WIZARD_STEPS.RECEIPT && stepData) {
            await receiptMutation.mutateAsync(stepData as ReceiptDto);
          }

          dispatch({ type: WizardActions.SAVE_SUCCESS });
        } catch (error) {
          const isTipNetworkError =
            currentStepId === WIZARD_STEPS.TIP && axios.isAxiosError(error) && error.code === 'ERR_NETWORK';
          const message = isTipNetworkError
            ? t('steps.tip.errors.noTerminal')
            : error instanceof Error
            ? error.message
            : 'Unknown error';

          if (isTipNetworkError) {
            onbLog.warn('wizard_tip_no_terminal', { 'onb.step': currentStepId });
          }

          onbLog.warn('wizard_step_save_failed', {
            'onb.step': currentStepId,
            'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
            'error.message': error instanceof Error ? error.message : String(error),
            'http.status_code': axios.isAxiosError(error) ? error.response?.status : undefined,
          });

          dispatch({ type: WizardActions.SAVE_FAILED, payload: { error: message } });
        } finally {
          span.end();
        }
      });
    },
    [
      currentStepId,
      isCompletionStep,
      dispatch,
      hubspotMutation,
      cashRegisterMutation,
      tipMutation,
      taxMutation,
      receiptMutation,
    ],
  );

  const handleBack = useCallback(() => {
    dispatch({ type: WizardActions.PREV_STEP });
  }, [dispatch]);

  const handleGoToStep = useCallback(
    (stepIndex: number) => {
      dispatch({ type: WizardActions.GOTO_STEP, payload: { step: stepIndex } });
    },
    [dispatch],
  );

  return {
    state,
    currentStepId,
    totalSteps: TOTAL_STEPS,
    isSaving: state.savingStatus === 'saving',
    savingError: state.savingError,
    isFirstStep: state.currentStep === 0,
    isLastStep: state.currentStep === TOTAL_STEPS - 1,
    isCompletionStep,
    handleNext,
    handleBack,
    handleGoToStep,
    applyTableServiceConfig: tableServiceMutation.mutateAsync,
    isApplyingTableService: tableServiceMutation.isLoading,
    applyTableServiceError: tableServiceMutation.error,
  };
};
